"use client";

import React from "react";
import { MapPin, House, ArrowRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PortageCountyCraftsmanship() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const buildSites = [{
    top: "25%",
    left: "35%",
    name: "Strategic HQ"
  }, {
    top: "60%",
    left: "20%",
    name: "Tech Hub"
  }, {
    top: "45%",
    left: "70%",
    name: "Data Center"
  }, {
    top: "80%",
    left: "55%",
    name: "Ops Center"
  }];
  return <section data-section-id="92825" id="seattle-headquarters" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={leftRef} className={`relative w-full aspect-square md:aspect-[4/3] lg:aspect-square rounded-2xl overflow-hidden border-4 border-primary shadow-2xl transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            {}
            <div className="absolute inset-0 bg-card">
              {}
              <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `linear-gradient(to right, var(--color-primary) 1px, transparent 1px), linear-gradient(to bottom, var(--color-primary) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }} />
              {}
              <div className="absolute top-0 left-0 w-full h-full opacity-20 bg-[radial-gradient(circle_at_20%_30%,_var(--color-primary)_0%,_transparent_40%),_radial-gradient(circle_at_80%_70%,_var(--color-accent)_0%,_transparent_50%)] mix-blend-overlay" />
            </div>

            {}
            <div className="absolute top-6 right-6 text-muted-foreground/50 flex flex-col items-center">
              <Compass className="size-8 mb-1" strokeWidth={1.5} />
              <span className="text-[10px] font-mono tracking-widest uppercase">98102</span>
            </div>

            {}
            {buildSites.map((site, i) => <div key={i} data-index={i} className="absolute flex flex-col items-center group" style={{
            top: site.top,
            left: site.left
          }}>
                <div className="relative flex items-center justify-center size-10 rounded-full bg-background border-2 border-primary shadow-lg z-10 transition-transform duration-300 group-hover:scale-110 group-hover:border-primary">
                  <House className="size-5 text-primary group-hover:text-primary transition-colors" />
                  {}
                  <div className="absolute inset-0 rounded-full border-2 border-primary opacity-0 group-hover:animate-ping" />
                </div>
                <div className="mt-2 px-3 py-1 bg-card text-primary text-xs font-mono font-bold tracking-wide rounded-md shadow-md opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 whitespace-nowrap pointer-events-none border border-primary/20">
                  {site.name}
                </div>
              </div>)}

            {}
            <div className="absolute bottom-6 left-6 bg-background/60 backdrop-blur-xl border border-primary/20 rounded-lg p-4 text-foreground">
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="size-5 text-primary" />
                <span className="font-sans text-lg font-bold tracking-tight">Seattle HQ</span>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span>Operational Nodes</span>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`flex flex-col justify-center space-y-8 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-mono tracking-widest uppercase border border-primary/20">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                Technical Oversight
              </div>
              <h2 className="text-5xl md:text-6xl font-sans font-bold text-foreground leading-tight tracking-tighter">
                Rooted in <br className="hidden md:block" />
                <span className="text-primary relative inline-block">
                  Seattle
                  <svg className="absolute w-full h-3 -bottom-1 left-0 text-primary opacity-50" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" />
                  </svg>
                </span>
              </h2>
            </div>

            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed border-l-4 border-primary pl-6 py-2">
              <p>
                Century Pointe Holdings, LLC operates as a specialized professional services firm at the heart of Seattle's technological ecosystem. We provide the technical oversight and strategic advisory necessary to navigate complex digital landscapes.
              </p>
              <p>
                By focusing on the intersection of modern technology and organizational efficiency, we help organizations streamline digital workflows and internal infrastructures. We serve as a versatile foundation for professional service delivery.
              </p>
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="h-14 px-8 text-base font-bold rounded-none border-2 border-primary bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-[4px_4px_0px_0px_var(--color-primary)] hover:shadow-[0px_0px_0px_0px_var(--color-primary)] hover:translate-x-[4px] hover:translate-y-[4px]">
                <Link to="/StrategicInquiriesPage">
                  Strategic Inquiries
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}